import { useState, useEffect } from "react";
import Select from "react-select";
import Button from "../../common/elements/Button";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";

const OP_SHORT = { IN: 'any of', GT: '>', GTE: '≥', LT: '<', LTE: '≤', BTWN: 'between' };

const AudienceModal = ({ isActive, experimentKey, isSubmitting, onClose, onSubmit }) => {
    const { execute } = useFetchWithMsal();
    const [loadingDropdown, setLoadingDropdown] = useState(false);
    const [dropdownOptions, setDropdownOptions] = useState([]);
    const [libraryMap, setLibraryMap] = useState({});
    const [selectedAudience, setSelectedAudience] = useState(null);
    const [isExclude, setIsExclude] = useState(false);
    const [isStrict, setIsStrict] = useState(false);

    useEffect(() => {
        if (!isActive) return;
        setSelectedAudience(null);
        setIsExclude(false);
        setIsStrict(false);
        setLoadingDropdown(true);

        Promise.all([
            execute('GET', thor_api_urls.experimentAudiencesForDropdown(experimentKey))
                .catch(() => null),
            execute('GET', thor_api_urls.audienceAttributes)
                .catch(() => null),
        ]).then(([dropdownData, libraryData]) => {
            const audiences = dropdownData?.experimentAudiences ?? [];
            setDropdownOptions(audiences.map(a => ({ value: a.audienceId, label: a.audienceName })));
            const map = {};
            (libraryData?.audienceAttributes ?? []).forEach(a => { map[a.audienceId] = a; });
            setLibraryMap(map);
        }).finally(() => setLoadingDropdown(false));
    }, [isActive, experimentKey, execute]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = () => {
        if (!selectedAudience) return;
        onSubmit({ audienceId: selectedAudience.value, isExclude, isStrict });
    };

    const preview = selectedAudience ? libraryMap[selectedAudience.value] : null;

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card">
                <header className="modal-card-head">
                    <p className="modal-card-title">Add Audience</p>
                    <button className="delete" onClick={onClose} />
                </header>
                <section className="modal-card-body">
                    {loadingDropdown ? <SmallLoadingSpinner /> : (
                        <>
                            <div className="field">
                                <label className="label">Audience</label>
                                <div className="control">
                                    <Select
                                        options={dropdownOptions}
                                        value={selectedAudience}
                                        onChange={setSelectedAudience}
                                        placeholder="Select an audience…"
                                        isSearchable
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </div>
                                {dropdownOptions.length === 0 && (
                                    <p className="help has-text-grey">All available audiences are already attached to this experiment.</p>
                                )}
                            </div>

                            {preview && (
                                <div className="box py-2 px-3 mb-0 mt-2" style={{ background: '#f5f7ff', border: '1px solid #d8e0ff' }}>
                                    <p className="is-size-7 has-text-grey mb-1">Audience definition</p>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <span className="tag is-light is-small" title="Attribute">{preview.attributeName}</span>
                                        <span className="tag is-info is-light is-small" title="Match type">
                                            {OP_SHORT[preview.audienceOperation] ?? preview.audienceOperation}
                                        </span>
                                        <span className="is-size-7 has-text-grey">
                                            {preview.audienceValueCount === 0
                                                ? <span className="tag is-danger is-light is-small">no values — audience won't match anyone</span>
                                                : `${preview.audienceValueCount} ${preview.audienceValueCount === 1 ? 'value' : 'values'}`}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="field mt-4">
                                <label className="label">Type</label>
                                <div className="buttons has-addons">
                                    <button
                                        type="button"
                                        className={`button is-small ${!isExclude ? 'is-success is-selected' : ''}`}
                                        onClick={() => setIsExclude(false)}
                                    >
                                        Include
                                    </button>
                                    <button
                                        type="button"
                                        className={`button is-small ${isExclude ? 'is-danger is-selected' : ''}`}
                                        onClick={() => setIsExclude(true)}
                                    >
                                        Exclude
                                    </button>
                                </div>
                                <p className="help">
                                    {isExclude
                                        ? 'Exclude: users matching this audience are explicitly removed from the experiment.'
                                        : 'Include: only users matching this audience are eligible. Add multiple audiences to AND conditions together.'}
                                </p>
                            </div>

                            <div className="field mt-4">
                                <label className="label">Strict Matching</label>
                                <div className="buttons has-addons">
                                    <button
                                        type="button"
                                        className={`button is-small ${!isStrict ? 'is-info is-selected' : ''}`}
                                        onClick={() => setIsStrict(false)}
                                    >
                                        Non-strict
                                    </button>
                                    <button
                                        type="button"
                                        className={`button is-small ${isStrict ? 'is-warning is-selected' : ''}`}
                                        onClick={() => setIsStrict(true)}
                                    >
                                        Strict
                                    </button>
                                </div>
                                <p className="help">
                                    {isStrict
                                        ? 'Strict: users without a recorded value for this attribute are excluded (absence = no match). Use when the attribute is always present for eligible users.'
                                        : 'Non-strict: users without a recorded value for this attribute are included (absence = pass-through). Use when the attribute may simply be missing for some legitimate users.'}
                                </p>
                            </div>
                        </>
                    )}
                </section>
                <footer className="modal-card-foot">
                    <Button
                        className="is-link mr-2"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !selectedAudience}
                    >
                        {isSubmitting ? 'Saving…' : 'Add'}
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </footer>
            </div>
        </div>
    );
};

export default AudienceModal;
